import React, { useEffect, useMemo, useState } from "react";

import { Feedback } from "../Feedback";
import * as S from "./styles";
import SimulatedHeader from "../Header";
import Icon from "../../../components/icon";
import {
  APROVA_180_CTA_URLS,
  getCurrentCoupon,
  getCouponCountdownMs,
} from "../../../core/storageKeys";

// Formata os milissegundos restantes do dia (SP) como HH:MM:SS.
const formatCountdown = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const getScoreCategory = (score) => {
  if (score <= 15) {
    return {
      color: "#D62828",
      foreground: "#fff",
      title: "EM TREINAMENTO",
      message: "Essa foi quase. Bora focar nos estudos até o dia da prova.",
      ctaUrl: APROVA_180_CTA_URLS.TREINAMENTO,
    };
  }
  if (score <= 30) {
    return {
      color: "#F7D518",
      foreground: "#0a0a0a",
      title: "RESERVA TÉCNICO",
      message: "Dá pra subir muito com plano certo.",
      ctaUrl: APROVA_180_CTA_URLS.RESERVA_TECNICO,
    };
  }
  if (score <= 45) {
    return {
      color: "#0E8A3E",
      foreground: "#fff",
      title: "TITULAR",
      message: "Tá no nível. Falta refinar pra cravar.",
      ctaUrl: APROVA_180_CTA_URLS.TITULAR,
    };
  }
  return {
    color: "#F4B400",
    foreground: "#0a0a0a",
    title: "CRAQUE - CAPITÃO",
    message: "Faltam só os detalhes pra cravar a nota.",
    ctaUrl: APROVA_180_CTA_URLS.CRAQUE,
  };
};

export const Result = ({
  user,
  ranking,
  feedback,
  match,
  history,
  average = {},
  beforeTimeSimulation,
  simulated,
  slug,
  serverTime,
}) => {
  const resolutions = feedback.resolutions || [];
  const correctAnswers = resolutions.filter((r) => r.isCorrect === true).length;
  const totalQuestions = resolutions.length;
  // Cenário 2: aluno não realizou o simulado -> exibe apenas o gabarito
  const hasAttempt = feedback.hasAttempt !== false;
  const category = getScoreCategory(correctAnswers);
  // Quem não fez a prova usa o CTA sem sufixo de categoria.
  const ctaUrl = hasAttempt ? category.ctaUrl : APROVA_180_CTA_URLS.NO_ATTEMPT;

  // Diferença (ms) entre o relógio do servidor (endpoint /time) e o relógio
  // local, ancorada uma vez. As validações de horário (contador e cupom do dia)
  // usam o tempo do servidor, não o do navegador do aluno — que pode estar
  // adiantado/atrasado. Enquanto o serverTime não chega, cai no relógio local.
  const serverOffset = useMemo(
    () => (typeof serverTime === "number" ? serverTime - Date.now() : 0),
    [serverTime],
  );

  // Tick de 1s só para re-renderizar; o "agora" é sempre derivado do servidor.
  // À meia-noite (SP) o contador zera e o cupom passa a ser o do dia seguinte.
  const [, setTick] = useState(0);
  const [playVideo, setPlayVideo] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setTick((tick) => tick + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const now = new Date(Date.now() + serverOffset);
  // const coupon = getCurrentCoupon(now);
  const coupon = { code: "CRAQUE10", percent: 10 };
  const countdown = `FALTAM ${formatCountdown(getCouponCountdownMs(now))}`;

  return (
    <S.Container>
      <SimulatedHeader simulated={simulated} title="Gabarito Simulado" />

      <S.MainWrapper>
        {hasAttempt && (
          <S.ScoreCard>
            <S.CategoryPill bg={category.color} fg={category.foreground}>
              <Icon icon="star" width={14} height={14} />
              {category.title}
            </S.CategoryPill>
            <S.ScoreRow>
              <S.ScoreValue color={category.color}>
                {correctAnswers}
              </S.ScoreValue>
              <S.ScoreTotal>de {totalQuestions} questões</S.ScoreTotal>
            </S.ScoreRow>
            <S.ScoreSubtitle>{category.message}</S.ScoreSubtitle>
          </S.ScoreCard>
        )}

        <S.VideoCard>
          <S.VideoPill>ANTES DE VER O GABARITO</S.VideoPill>
          <S.VideoTitle>
            TRI, Testlets, Redação.
            <br />O que o ENEM 2026 vai cobrar.
          </S.VideoTitle>
          <S.VideoDescription>
            Os craques do Aprova mostram o que mudou na lógica da prova, como o
            TRI impacta sua nota e o que alunos fortes fazem diferente.
          </S.VideoDescription>
          <S.VideoPlayer>
            {playVideo ? (
              <S.VideoFrame
                src="https://player.vimeo.com/video/1196435799?autoplay=1"
                title="O jogo por trás do ENEM"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <S.VideoThumb onClick={() => setPlayVideo(true)}>
                <S.VideoLabel>O jogo por trás do ENEM</S.VideoLabel>
                <S.PlayButton>
                  <Icon icon="play" width={28} height={28} />
                </S.PlayButton>
              </S.VideoThumb>
            )}
          </S.VideoPlayer>
        </S.VideoCard>

        <S.Aprova180Card>
          <S.Aprova180Image />
          <S.Aprova180Content>
            <S.Aprova180Eyebrow>
              APROVA ENEM 180 <S.EyebrowDot>·</S.EyebrowDot> PRA QUEM QUER VIRAR
              O JOGO
            </S.Aprova180Eyebrow>
            <S.Aprova180Title>
              A virada que faltava
              <br />
              pra você ser aprovado.
            </S.Aprova180Title>
            <S.Aprova180Description>
              Sai da arquibancada. Entra em campo. 20 semanas com o mesmo time
              do Aprova te treinando até o Enem.
            </S.Aprova180Description>
            <S.BulletList>
              <S.BulletItem>
                <S.BulletIcon>✓</S.BulletIcon>
                Aulas Objetivas: direto ao ponto
              </S.BulletItem>
              <S.BulletItem>
                <S.BulletIcon>✓</S.BulletIcon>
                Plano de estudo organizado por bloco
              </S.BulletItem>
              <S.BulletItem>
                <S.BulletIcon>✓</S.BulletIcon>
                Time titular dos Craques com você
              </S.BulletItem>
              <S.BulletItem>
                <S.BulletIcon>✓</S.BulletIcon>
                Redação com correção 100% humana e simulado semanal
              </S.BulletItem>
            </S.BulletList>
            <S.Aprova180Footer>
              {coupon && (
                <S.CouponBadge>
                  <S.CouponLabel>Cupom</S.CouponLabel>
                  <S.CouponCode>{coupon.code}</S.CouponCode>
                  <S.CouponDiscount>−{coupon.percent}%</S.CouponDiscount>
                </S.CouponBadge>
              )}
              <S.Aprova180CTA href={ctaUrl} target="_blank" rel="noreferrer">
                {coupon ? "Garantir minha vaga →" : "Matrículas abertas"}
              </S.Aprova180CTA>
            </S.Aprova180Footer>
          </S.Aprova180Content>
        </S.Aprova180Card>

        <S.Divider>
          <S.DividerLine />
          <S.DividerPill>FIM DO 1º TEMPO · AGORA A CORREÇÃO ↓</S.DividerPill>
          <S.DividerLine />
        </S.Divider>
      </S.MainWrapper>

      <Feedback
        pdfUrl={feedback.pdfUrl}
        feedback={resolutions}
        hasAttempt={hasAttempt}
        beforeTimeSimulation={beforeTimeSimulation}
        isRanking={feedback.isRanking}
        slug={slug}
        history={history}
        couponUrl={ctaUrl}
        couponCode={coupon ? coupon.code : null}
        couponDiscount={coupon ? `${coupon.percent}% OFF` : null}
      />
    </S.Container>
  );
};
